import { useState, type FormEvent } from "react";
import { ArrowRight, CheckCircle2, LogOut } from "lucide-react";

import { RaqozLogo } from "@/components/brand/raqoz-logo";
import { FieldError } from "@/components/field-error";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { selfServiceCreateOrganisation } from "@/lib/api";

export function OrganisationOnboarding({
  userName,
  onDone,
  onSignOut,
}: {
  userName: string;
  onDone: () => void;
  onSignOut: () => void;
}) {
  const [countryCode, setCountryCode] = useState("IN");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      await selfServiceCreateOrganisation({
        name: String(form.get("name") || ""),
        legalName: String(form.get("legalName") || ""),
        countryCode,
        taxIdentifier: String(form.get("taxIdentifier") || ""),
      });
      onDone();
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Unable to create your workspace.",
      );
    } finally {
      setBusy(false);
    }
  }
  const taxLabel =
    countryCode === "IN"
      ? "GSTIN (optional)"
      : "Tax registration number (optional)";
  return (
    <main className="grid min-h-screen bg-[var(--canvas)] lg:grid-cols-[.85fr_1.15fr]">
      <section className="hidden flex-col justify-between bg-[var(--nav-surface)] p-16 text-[var(--nav-text)] lg:flex">
        <RaqozLogo inverse />
        <div>
          <p className="text-xs font-semibold tracking-[.12em] text-[var(--nav-muted)]">
            WELCOME TO RAQOZ
          </p>
          <h1 className="mt-4 max-w-xl text-5xl font-semibold leading-[1.05]">
            Create the accountable home for every purchase.
          </h1>
          <p className="mt-6 max-w-lg text-lg leading-8 text-[var(--nav-muted)]">
            Your Launch workspace includes one organisation, one location and
            the complete controlled procurement journey.
          </p>
          <ol className="mt-8 grid gap-3 text-sm">
            {["Create your secure workspace", "Add the people and controls", "Complete your first purchase"].map((step, index) => (
              <li className="flex items-center gap-3" key={step}><span className="grid size-7 place-items-center rounded-full border border-[var(--nav-muted)] tabular-nums">{index + 1}</span>{step}</li>
            ))}
          </ol>
        </div>
        <p className="text-xs text-[var(--nav-muted)]">
          Verified self-service onboarding · Free Launch plan
        </p>
      </section>
      <section className="flex min-h-screen flex-col">
        <header className="flex h-20 items-center justify-between px-6 lg:justify-end">
          <div className="lg:hidden">
            <RaqozLogo />
          </div>
          <Button variant="ghost" onClick={onSignOut}>
            <LogOut className="size-4" />
            Sign out
          </Button>
        </header>
        <div className="grid flex-1 place-items-center px-5 pb-20">
          <form
            onSubmit={submit}
            className="surface grid w-full max-w-xl gap-5 p-7 sm:p-10"
          >
            <div>
              <p className="eyebrow">SET UP YOUR ORGANISATION</p>
              <h2 className="mt-2 text-3xl font-semibold">
                Create your workspace
              </h2>
              <p className="mt-3 text-sm leading-6 text-[var(--text-secondary)]">
                Signed in as {userName}. RAQOZ will add the head-office
                location, reporting profile, approval policy and free Launch
                entitlement.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Label>
                Organisation name
                <Input name="name" autoFocus required minLength={2} />
              </Label>
              <Label>
                Legal name (optional)
                <Input name="legalName" />
              </Label>
              <Label>
                Country
                <Select
                  name="countryCode"
                  value={countryCode}
                  onChange={(event) => setCountryCode(event.target.value)}
                >
                  <option value="IN">India</option>
                  <option value="AE">United Arab Emirates</option>
                  <option value="SG">Singapore</option>
                  <option value="GB">United Kingdom</option>
                  <option value="AU">Australia</option>
                  <option value="US">United States</option>
                  <option value="ZZ">Other</option>
                </Select>
              </Label>
              <Label>
                {taxLabel}
                <Input name="taxIdentifier" />
              </Label>
            </div>
            <div className="rounded-xl border border-[var(--border)] bg-[var(--surface-secondary)] p-4 text-sm leading-6">
              <p className="flex items-center gap-2 font-semibold"><CheckCircle2 className="size-4" />Launch plan · Free</p>
              <p className="mt-1 text-[var(--text-secondary)]">You become the tenant administrator. RAQOZ creates the head office, country reporting profile and starter approval controls, then guides you through the remaining setup.</p>
            </div>
            <FieldError>{error}</FieldError>
            <Button disabled={busy} type="submit" className="h-11 w-full">
              {busy
                ? "Creating secure workspace…"
                : "Create my RAQOZ workspace"}
              <ArrowRight className="size-4" />
            </Button>
          </form>
        </div>
      </section>
    </main>
  );
}
